// Drawing Guess on screen (desktop + phone): the join prompt, the lobby with
// the options (rounds, draw time, word difficulty), then the game — the
// drawing (draw on it with mouse / touch when it's your turn), the hint and
// timer, the guess box and feed for everyone else, and the scores. VR uses
// DrawingVRPanel. Mounted by GameOverlay.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tableAction } from '../gameStore';
import { JoinPrompt, SeatList, LobbyActions, HelpButton, GameOverBlock, PanelError, TurnTimer, leaveTable } from '../kit/LobbyKit';
import { Leaderboard } from '../Leaderboard';
import { BoardPainter, COLORS, COLOR_NAMES, SIZES, SIZE_NAMES, getBoard, penFor, usePenStyle, useStrokeCount } from './strokes';
import { chatLineText, hintText, myStatus, nameAt, phaseDeadline, standings, type DrawPriv, type DrawState } from './drawingView';
import { DEFAULT_OPTIONS, DIFFICULTIES, DRAW_SECONDS, GUESS_MAX_LEN, ROUND_OPTIONS, type DrawOptions } from '../../../../supabase/functions/games/drawing.ts';
import type { OverlayProps } from '../types';
import './DrawingOverlay.css';

const stopKeys = {
  // The viewer moves the avatar on WASD / space from window listeners: keep typing to ourselves.
  onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
  onKeyUp: (e: React.KeyboardEvent) => e.stopPropagation(),
  onKeyPress: (e: React.KeyboardEvent) => e.stopPropagation(),
};

const DIFF_LABEL: Record<string, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard', mixed: 'Mixed' };

/** The drawing, live. Draws with mouse / touch / pen when `canDraw`. */
export function DrawCanvas({ worldId, tableId, canDraw }: { worldId: string; tableId: string; canDraw: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const active = useRef<number | null>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const painter = new BoardPainter(c);
    painter.paint(getBoard(tableId), true);
    let raf = 0;
    const loop = () => { painter.paint(getBoard(tableId)); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [tableId]);
  const pen = canDraw ? penFor(worldId, tableId) : null;
  useEffect(() => () => { if (active.current !== null) pen?.up(); active.current = null; }, [pen]);
  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height] as const;
  };
  return (
    <canvas ref={ref} width={900} height={600}
      className={`gx-dr-canvas ${canDraw ? 'is-drawing' : ''}`}
      aria-label={canDraw ? 'Drawing canvas — draw here' : 'The drawing'}
      onPointerDown={pen ? (e) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        active.current = e.pointerId;
        const [x, y] = at(e); pen.down(x, y);
      } : undefined}
      onPointerMove={pen ? (e) => {
        if (active.current !== e.pointerId) return;
        // Coalesced events keep fast strokes smooth.
        const evs = (e.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [e.nativeEvent];
        const r = e.currentTarget.getBoundingClientRect();
        for (const ev of evs.length ? evs : [e.nativeEvent]) pen.move((ev.clientX - r.left) / r.width, (ev.clientY - r.top) / r.height);
      } : undefined}
      onPointerUp={pen ? (e) => { if (active.current === e.pointerId) { active.current = null; pen.up(); } } : undefined}
      onPointerCancel={pen ? () => { active.current = null; pen.up(); } : undefined}
    />
  );
}

function Tools({ worldId, tableId }: { worldId: string; tableId: string }) {
  const { color, size, setColor, setSize } = usePenStyle();
  const empty = useStrokeCount(tableId) === 0;
  const pen = penFor(worldId, tableId);
  return (
    <div className="gx-dr-tools" role="toolbar" aria-label="Drawing tools">
      <div className="gx-dr-swatches">
        {COLORS.map((c, i) => (
          <button key={c} className={`gx-dr-swatch ${color === i ? 'is-on' : ''}`} style={{ background: c }}
            aria-label={COLOR_NAMES[i]} aria-pressed={color === i} onClick={() => setColor(i)} />
        ))}
      </div>
      <div className="gx-dr-toolrow">
        {SIZES.map((s, i) => (
          <button key={s} className={`gx-dr-size ${size === i ? 'is-on' : ''}`} aria-label={`${SIZE_NAMES[i]} brush`} aria-pressed={size === i} onClick={() => setSize(i)}>
            <i style={{ width: 4 + i * 6, height: 4 + i * 6, background: COLORS[color] }} />
          </button>
        ))}
        <button className="gx-sp-btn gx-sp-btn--ghost" disabled={empty} onClick={() => pen.undo()}>Undo</button>
        <button className="gx-sp-btn gx-sp-btn--ghost" disabled={empty} onClick={() => pen.clear()}>Clear</button>
      </div>
    </div>
  );
}

function GuessBox({ pid, busy }: { pid: string; busy: boolean }) {
  const [text, setText] = useState('');
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    await tableAction(pid, 'move', { move: { type: 'guess', text: t } });
  };
  return (
    <form className="gx-dr-guess" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      <input className="gx-dr-input" value={text} maxLength={GUESS_MAX_LEN} placeholder="Type your guess…" aria-label="Your guess"
        autoComplete="off" autoCorrect="off" spellCheck={false} enterKeyHint="send"
        onChange={(e) => setText(e.target.value)} {...stopKeys} />
      <button className="gx-sp-btn gx-sp-btn--primary" type="submit" disabled={busy || !text.trim()}>Guess</button>
    </form>
  );
}

function Feed({ g }: { g: DrawState }) {
  const ref = useRef<HTMLDivElement>(null);
  const last = g.chat.at(-1)?.n;
  useEffect(() => { const el = ref.current; if (el) el.scrollTop = el.scrollHeight; }, [last]);
  return (
    <div className="gx-dr-feed" ref={ref} aria-live="polite">
      {g.chat.slice(-20).map((l) => (
        <div key={l.n} className={`gx-dr-line is-${l.kind}`}>{chatLineText(l)}</div>
      ))}
    </div>
  );
}

function Scores({ st, seat, final = false }: { st: OverlayProps['table']['state']; seat: number; final?: boolean }) {
  return (
    <ol className="gx-dr-scores">
      {standings(st).map((r, i) => (
        <li key={r.seat} className={`${r.seat === seat ? 'is-me' : ''} ${!final && r.guessed ? 'is-guessed' : ''}`}>
          <span>{final ? `${i + 1}. ` : r.drawer ? '✏ ' : r.guessed ? '✓ ' : ''}{r.name}{r.seat === seat ? ' (you)' : ''}</span>
          <b>{r.score}{!final && r.turn ? <small> +{r.turn}</small> : null}</b>
        </li>
      ))}
    </ol>
  );
}

function OptionRow<T extends string | number>({ label, values, current, fmt, onPick, disabled }: {
  label: string; values: readonly T[]; current: T; fmt: (v: T) => string; onPick: (v: T) => void; disabled: boolean;
}) {
  return (
    <div className="gx-dr-opt">
      <span className="gx-sp-seat-pos">{label}</span>
      <div className="gx-dr-seg" role="group" aria-label={label}>
        {values.map((v) => (
          <button key={String(v)} className={`gx-dr-segbtn ${v === current ? 'is-on' : ''}`} aria-pressed={v === current}
            disabled={disabled} onClick={() => v !== current && onPick(v)}>{fmt(v)}</button>
        ))}
      </div>
    </div>
  );
}

function Lobby(props: OverlayProps) {
  const { def, pid, entry, table, worldId, onHelp } = props;
  const [board, setBoard] = useState(false);
  const o: DrawOptions = { ...DEFAULT_OPTIONS, ...(table.state.options ?? {}) };
  const set = (patch: Partial<DrawOptions>) => void tableAction(pid, 'configure', { options: patch });
  return (
    <div className="gx-sp gx-sp--panel gx-dr" role="region" aria-label={`${def.label} table`}>
      <div className="gx-sp-head">
        <span className="gx-sp-prompt-title"><span className="gx-sp-spade">{def.glyph}</span> {def.label}</span>
        <div className="gx-sp-head-actions">
          <button className={`gx-sp-btn gx-sp-btn--ghost ${board ? 'is-on' : ''}`} onClick={() => setBoard((v) => !v)} aria-pressed={board}>Leaderboard</button>
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>
      {board ? <Leaderboard kind={def.kind} worldId={worldId} compact title={false} limit={8} /> : <SeatList {...props} />}
      <div className="gx-dr-opts">
        <OptionRow label="Rounds" values={ROUND_OPTIONS} current={o.rounds as 2 | 3} fmt={String} onPick={(rounds) => set({ rounds })} disabled={entry.busy} />
        <OptionRow label="Draw time" values={DRAW_SECONDS} current={o.drawSec as 60 | 80 | 100} fmt={(s) => `${s}s`} onPick={(drawSec) => set({ drawSec })} disabled={entry.busy} />
        <OptionRow label="Words" values={DIFFICULTIES} current={o.difficulty} fmt={(d) => DIFF_LABEL[d]} onPick={(difficulty) => set({ difficulty })} disabled={entry.busy} />
      </div>
      <LobbyActions {...props} />
      <p className="gx-sp-note">Everyone takes a turn drawing; the rest race to guess. Friends can join any time. <HelpButton def={def} onHelp={onHelp} /></p>
      <PanelError entry={entry} />
    </div>
  );
}

export function DrawingOverlay(props: OverlayProps) {
  const { def, pid, worldId, entry, table, seat, onHelp } = props;
  const st = table.state;
  const g = (st.g ?? null) as DrawState | null;
  const priv = (entry.priv ?? null) as DrawPriv | null;

  // "So close!" — only you see it, for a moment after each close guess.
  const [close, setClose] = useState<string | null>(null);
  const closeN = priv?.closeN ?? 0;
  useEffect(() => {
    if (!closeN || !priv?.close) return;
    setClose(priv.close);
    const t = window.setTimeout(() => setClose(null), 2600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeN]);
  const turnKey = g ? `${st.gameId}:${g.turnNo}` : '';
  useEffect(() => setClose(null), [turnKey]);
  const deadline = useMemo(() => phaseDeadline(st), [st]);

  if (seat < 0) return <JoinPrompt {...props} />;
  if (st.phase === 'lobby') return <Lobby {...props} />;
  if (!g) return null;

  const playing = st.phase === 'playing';
  const amDrawer = playing && g.drawer === seat && (g.phase === 'choosing' || g.phase === 'drawing');
  const drawingNow = playing && g.phase === 'drawing';
  const canGuess = drawingNow && !amDrawer && !priv?.guessed;
  const showHint = playing && (g.phase === 'drawing' || g.phase === 'reveal') && g.hint.length > 0;

  let big: React.ReactNode;
  if (amDrawer && g.phase === 'drawing') big = <span className="gx-dr-word">Draw: <b>{priv?.word ?? '…'}</b></span>;
  else if (showHint) big = <span className="gx-dr-hint" aria-label={`Hint: ${g.hint.length} characters`}>{hintText(g.hint)}</span>;
  else big = <span className="gx-dr-status">{myStatus(st, seat, priv)}</span>;

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game gx-dr ${amDrawer ? 'is-my-turn' : ''}`} role="region" aria-label="Drawing Guess game">
      <div className="gx-sp-head">
        <span className="gx-dr-round">{st.phase === 'over' ? 'Final scores' : `Round ${g.round}/${g.rounds}`}</span>
        <div className="gx-sp-head-actions">
          {playing && <TurnTimer deadline={deadline} />}
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>

      {st.phase !== 'over' && <div className="gx-dr-top">{big}</div>}
      {g.phase === 'drawing' && !amDrawer && priv?.guessed && <div className="gx-dr-flash is-good">You got it — <b>{priv.word}</b>! Others are still guessing.</div>}
      {close && canGuess && <div className="gx-dr-flash is-close">“{close}” — so close!</div>}
      {playing && g.phase === 'reveal' && (
        <div className="gx-dr-flash">{g.reveal?.word ? <>The word was <b>{g.reveal.word}</b></> : 'Turn skipped'}
          {g.turnPts[seat] ? <> · you +{g.turnPts[seat]}</> : null}</div>
      )}

      {amDrawer && g.phase === 'choosing' ? (
        <div className="gx-dr-choices" role="group" aria-label="Pick a word to draw">
          {(priv?.choices ?? []).map((w, i) => (
            <button key={w} className="gx-sp-btn gx-dr-choice" disabled={entry.busy}
              onClick={() => void tableAction(pid, 'move', { move: { type: 'choose', index: i } })}>
              <small>{g.difficulty === 'mixed' ? ['Easy', 'Medium', 'Hard'][i] : DIFF_LABEL[g.difficulty]}</small>{w}
            </button>
          ))}
        </div>
      ) : st.phase !== 'over' && table.id ? (
        <DrawCanvas worldId={worldId} tableId={table.id} canDraw={amDrawer && drawingNow} />
      ) : null}

      {amDrawer && drawingNow && <Tools worldId={worldId} tableId={table.id} />}
      {canGuess && <GuessBox pid={pid} busy={entry.busy} />}

      {st.phase === 'over' ? (
        <>
          <Scores st={st} seat={seat} final />
          <GameOverBlock {...props} summary={g.winners.includes(seat) ? 'You win!' : `${g.winners.map((s) => nameAt(st, s)).join(' & ')} win${g.winners.length > 1 ? '' : 's'}`} />
        </>
      ) : (
        <div className="gx-dr-bottom">
          <Feed g={g} />
          <Scores st={st} seat={seat} />
        </div>
      )}
      <PanelError entry={entry} />
    </div>
  );
}

export default DrawingOverlay;
